import { DateTime } from 'luxon';
import { DateFilterEnum } from '@/ui';

/**
 * The reports date filter, round-tripped through the `?t=` query string so a
 * report can be linked to, bookmarked and refreshed.
 *
 * Every range is resolved against "today" in the viewer's reporting timezone
 * rather than the browser's, so two people looking at the same company see the
 * same days.
 */

export type ReportDateRange = { from: Date; to: Date };

export type ReportDateFilter = {
    preset: DateFilterEnum;
    range: ReportDateRange;
};

const DEFAULT_QUERY_STRING = '4w';
const DATE_RANGE_PATTERN = /from_(\d{4}-\d{2}-\d{2})_to_(\d{4}-\d{2}-\d{2})/;

function getToday(timezone: string): DateTime {
    return DateTime.now().setZone(timezone).startOf('day');
}

export function getDateFilterFromQueryString(params: {
    value: string | null | undefined;
    defaultValue?: string;
    earliestDate: Date;
    timezone: string;
}): ReportDateFilter {
    const {
        value: _value,
        defaultValue = DEFAULT_QUERY_STRING,
        earliestDate,
        timezone
    } = params;

    const value = _value || defaultValue;
    const today = getToday(timezone);

    let preset: DateFilterEnum;
    let range: ReportDateRange;

    if (value === '1d') {
        const todayDate = today.toJSDate();
        preset = DateFilterEnum.Today;
        range = { from: todayDate, to: todayDate };
    } else if (value === '7d') {
        preset = DateFilterEnum.Last7Days;
        range = {
            from: today.minus({ days: 7 }).toJSDate(),
            to: today.toJSDate()
        };
    } else if (value === '4w') {
        preset = DateFilterEnum.Last4Weeks;
        range = {
            from: today.minus({ days: 7 * 4 }).toJSDate(),
            to: today.toJSDate()
        };
    } else if (value === '3m') {
        preset = DateFilterEnum.Last3Months;
        range = {
            from: today.minus({ months: 3 }).toJSDate(),
            to: today.toJSDate()
        };
    } else if (value === '6m') {
        preset = DateFilterEnum.Last6Months;
        range = {
            from: today.minus({ months: 6 }).toJSDate(),
            to: today.toJSDate()
        };
    } else if (value === '12m') {
        preset = DateFilterEnum.Last12Months;
        range = {
            from: today.minus({ months: 12 }).toJSDate(),
            to: today.toJSDate()
        };
    } else if (value === 'm') {
        preset = DateFilterEnum.MonthToDate;
        range = {
            from: today.startOf('month').toJSDate(),
            to: today.toJSDate()
        };
    } else if (value === 'y') {
        preset = DateFilterEnum.YearToDate;
        range = {
            from: today.startOf('year').toJSDate(),
            to: today.toJSDate()
        };
    } else if (value === 'all') {
        preset = DateFilterEnum.AllTime;
        range = { from: earliestDate, to: today.toJSDate() };
    } else if (value && DATE_RANGE_PATTERN.test(value)) {
        const [, from, to] = value.match(DATE_RANGE_PATTERN)!;
        const [fromYear, fromMonth, fromDay] = from.split('-').map(Number);
        const [toYear, toMonth, toDay] = to.split('-').map(Number);

        const fromDate = DateTime.fromObject(
            { year: fromYear, month: fromMonth, day: fromDay },
            { zone: timezone }
        ).toJSDate();
        const toDate = DateTime.fromObject(
            { year: toYear, month: toMonth, day: toDay },
            { zone: timezone }
        ).toJSDate();

        preset = DateFilterEnum.Custom;
        if (fromDate.getTime() > toDate.getTime()) {
            range = { from: toDate, to: fromDate };
        } else {
            range = { from: fromDate, to: toDate };
        }
    } else {
        preset = DateFilterEnum.AllTime;
        range = { from: earliestDate, to: today.toJSDate() };
    }

    return { preset, range };
}

export function getDateRangeFromPreset(
    preset: DateFilterEnum,
    customRange: ReportDateRange | null,
    earliestDate: Date,
    timezone: string
): ReportDateRange {
    const today = getToday(timezone);

    switch (preset) {
        case DateFilterEnum.Today: {
            const todayDate = today.toJSDate();
            return { from: todayDate, to: todayDate };
        }
        case DateFilterEnum.Last7Days: {
            return {
                from: today.minus({ days: 7 }).toJSDate(),
                to: today.toJSDate()
            };
        }
        case DateFilterEnum.Last4Weeks: {
            return {
                from: today.minus({ days: 7 * 4 }).toJSDate(),
                to: today.toJSDate()
            };
        }
        case DateFilterEnum.Last3Months: {
            return {
                from: today.minus({ months: 3 }).toJSDate(),
                to: today.toJSDate()
            };
        }
        case DateFilterEnum.Last6Months: {
            return {
                from: today.minus({ months: 6 }).toJSDate(),
                to: today.toJSDate()
            };
        }
        case DateFilterEnum.Last12Months: {
            return {
                from: today.minus({ months: 12 }).toJSDate(),
                to: today.toJSDate()
            };
        }
        case DateFilterEnum.MonthToDate: {
            return {
                from: today.startOf('month').toJSDate(),
                to: today.toJSDate()
            };
        }
        case DateFilterEnum.YearToDate: {
            return {
                from: today.startOf('year').toJSDate(),
                to: today.toJSDate()
            };
        }
        case DateFilterEnum.Custom:
            if (customRange) {
                return customRange;
            } else {
                return {
                    from: today.minus({ days: 7 }).toJSDate(),
                    to: today.toJSDate()
                };
            }
        case DateFilterEnum.AllTime:
        default: {
            return { from: earliestDate, to: today.toJSDate() };
        }
    }
}

export function getDateFilterQueryString(
    timezone: string,
    type: DateFilterEnum,
    range?: ReportDateRange | null
): string {
    switch (type) {
        case DateFilterEnum.Today:
            return '1d';
        case DateFilterEnum.Last7Days:
            return '7d';
        case DateFilterEnum.Last4Weeks:
            return '4w';
        case DateFilterEnum.Last3Months:
            return '3m';
        case DateFilterEnum.Last6Months:
            return '6m';
        case DateFilterEnum.Last12Months:
            return '12m';
        case DateFilterEnum.MonthToDate:
            return 'm';
        case DateFilterEnum.YearToDate:
            return 'y';
        case DateFilterEnum.Custom:
            if (range) {
                const fromDt = DateTime.fromJSDate(range.from).setZone(
                    timezone
                );
                const toDt = DateTime.fromJSDate(range.to).setZone(timezone);
                return `from_${fromDt.toFormat('yyyy-MM-dd')}_to_${toDt.toFormat('yyyy-MM-dd')}`;
            } else {
                return '7d';
            }
        case DateFilterEnum.AllTime:
        default:
            return 'all';
    }
}
